export const formatDuration = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

export const formatDate = (dateString: string, locale: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return date.toLocaleDateString(locale, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : {year: 'numeric'}),
  });
};

export const formatTime = (dateString: string, locale: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString(locale, {hour: '2-digit', minute: '2-digit'});
};

/** Blob → base64 payload (data-URL prefix stripped). */
export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });

export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

/**
 * Guard for OAuth popup flows: when the user closes the popup without
 * finishing, some browsers/COOP setups delay or swallow the SDK's
 * popup-closed rejection, leaving the UI stuck in its busy state. This
 * releases the busy state shortly after focus returns to the main window
 * (the popup is gone either way — on success the auth state flips anyway).
 * Returns a cleanup function; call it when the flow settles.
 */
export const releaseBusyOnRefocus = (
  release: () => void,
  delayMs = 1500,
): (() => void) => {
  let timer: number | null = null;
  const onFocus = () => {
    if (timer !== null) {
      window.clearTimeout(timer);
    }
    timer = window.setTimeout(release, delayMs);
  };
  window.addEventListener('focus', onFocus);
  return () => {
    window.removeEventListener('focus', onFocus);
    if (timer !== null) {
      window.clearTimeout(timer);
    }
  };
};

export const classNames = (
  ...parts: Array<string | false | null | undefined>
): string => parts.filter(Boolean).join(' ');
